import React, { useEffect, useRef, useReducer } from "react";
import { consoleManager, type LogMessage } from "../utils/ConsoleManager";

const LOG_STYLES = {
  log: "text-gray-300",
  error: "text-red-400",
  warn: "text-yellow-400",
  debug: "text-purple-400",
  info: "text-blue-400",
} as const;

const LOG_ICONS = {
  log: "💬",
  error: "❌",
  warn: "⚠️",
  debug: "🔍",
  info: "ℹ️",
} as const;

type LogsAction =
  | { type: "SET_LOGS"; logs: LogMessage[] }
  | { type: "CLEAR_LOGS" };

function logsReducer(state: LogMessage[], action: LogsAction): LogMessage[] {
  switch (action.type) {
    case "SET_LOGS":
      return [...action.logs];
    case "CLEAR_LOGS":
      return [];
    default:
      return state;
  }
}

export const Console: React.FC = () => {
  const [logs, dispatch] = useReducer(logsReducer, []);
  const consoleRef = useRef<HTMLDivElement>(null);
  const mounted = useRef(false);
  const shouldScrollRef = useRef(true);

  useEffect(() => {
    mounted.current = true;

    const unsubscribe = consoleManager.subscribe((newLogs) => {
      if (mounted.current) {
        dispatch({ type: "SET_LOGS", logs: newLogs });
      }
    });

    return () => {
      mounted.current = false;
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (consoleRef.current && shouldScrollRef.current) {
      consoleRef.current.scrollTop = consoleRef.current.scrollHeight;
    }
  }, [logs]);

  const handleScroll = () => {
    if (!consoleRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = consoleRef.current;
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 50;
    shouldScrollRef.current = isAtBottom;
  };

  const clearLogs = () => {
    dispatch({ type: "CLEAR_LOGS" });
  };

  return (
    <div className="flex flex-col h-[900px]">
      <div className="flex justify-between items-center bg-gray-900 p-4 border-b border-gray-700">
        <span className="font-semibold">Console Output ({logs.length})</span>
        <button
          onClick={clearLogs}
          className="px-3 py-1 text-sm bg-gray-800 hover:bg-gray-700 rounded-md transition-colors"
        >
          Clear
        </button>
      </div>

      <div
        ref={consoleRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto font-mono text-sm bg-gray-900 p-4 scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent"
      >
        {logs.map((log, index) => (
          <div
            key={index}
            className={`mb-2 ${LOG_STYLES[log.type]} flex items-start gap-2`}
          >
            <span className="flex-shrink-0 min-w-[4.5rem] text-gray-500">
              {log.timestamp.toLocaleTimeString()}
            </span>
            <span className="flex-shrink-0 w-6">{LOG_ICONS[log.type]}</span>
            <span className="whitespace-pre-wrap break-words flex-1">
              {log.message}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};
